import React from "react";
import Storyblok, { version } from "../../lib/storyblok-client";
import { relationsToResolve } from "@/constants/general";
import {
  GetBlogStories,
  getBlogStories,
  getCurrentPaths,
} from "@/lib/storyblok-gapi";
import Components from "../../components/components";
import { getSeoFromStory } from "@/lib/seo";
import { useStoryblok } from "../../helpers/storyblok-hook";
import { isProduction } from "../../helpers/environment";
import { Story } from "@/types/storyblok";
import { GetStaticPropsContext } from "next";

interface Props {
  story?: Story;
  otherBlogs: GetBlogStories[];
}

export default function Blog({ story, otherBlogs }: Props) {
  story = useStoryblok(story, !isProduction);
  return (
    story &&
    React.createElement(Components(story?.content?.component), {
      key: story?.uuid,
      content: story?.content,
      published: story.first_published_at,
      full_slug: story?.full_slug,
      name: story?.name,
      otherBlogs,
    })
  );
}

export async function getStaticPaths() {
  const {
    BlogItems: { items },
  } = await getCurrentPaths({ name: "BlogItems" });

  const paths = items.map((item) => ({ params: { slug: item.slug } }));

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps(context: GetStaticPropsContext) {
  const { params: { slug } = { slug: "" } } = context;

  try {
    const {
      data: { story },
    } = await Storyblok.get(`cdn/stories/blog/${slug}`, {
      resolve_relations: relationsToResolve,
      version,
    });
    const otherBlogs = await getBlogStories();
    const { items = [] } = otherBlogs?.BlogItems ?? {};
    const filteredOtherBlogs =
      items.length > 0 &&
      items.filter((item) => item.id !== story.id).slice(0, 4);

    return {
      props: {
        story,
        otherBlogs: filteredOtherBlogs,
        seo: await getSeoFromStory({ story }),
        revalidate: 86400,
      },
    };
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
}
